import { DialogContentText, makeStyles } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import { get } from 'lodash'
import { useState } from 'react'
import * as React from 'react'
import {
  Button as RaButton,
  FileField,
  FileInput,
  Identifier,
  Record as RaRecord,
  required,
  useNotify,
  useRefresh,
} from 'react-admin'
import { Form } from 'react-final-form'
import { VerticalDivider } from 'shared/components/Base/Divider'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { Dict } from 'styled-components/dist/types'

import { captureExceptionIfProduction } from 'helpers/utils'

interface Choice {
  id: string
  name: string
}

interface BaseUploadButtonFormDataInput {
  name: string
  input: any
  choices?: Choice[]
  validate: any
  value?: any
  className?: any
  label?: string
  size?: number
  isTop?: boolean
  reference?: string
  condition?: (values: any) => boolean
  onChange?: (event: any) => void
}

interface BaseUploadFileInput {
  name: string | undefined
  label?: string
  value?: string | null
}

interface BaseUploadButtonProps {
  record?: RaRecord
  fileFieldInputs: BaseUploadFileInput[]
  formDataInputs?: BaseUploadButtonFormDataInput[]
  uploadFunction: (id: Identifier, formData: FormData) => Promise<void>
  dialogTitle: string
  buttonLabel: string
  deleteFunction?: (id: Identifier) => Promise<void>
  multiple: boolean
  additionalPayloadData?: Object
  buttonVariant?: 'text' | 'outlined' | 'contained' | undefined
  className?: string
  buttonClassNames?: string
  buttonIcon?: React.ReactElement
  Warning?: React.ReactNode
}

function getInputGridItem(formInput: BaseUploadButtonFormDataInput, idx: number, isTop: boolean) {
  const Input = formInput.input
  const size: any = formInput.size ? formInput?.size : 5
  const conditionalProps: Dict = {}

  if (Input === Checkbox) {
    conditionalProps['value'] = formInput.value
    conditionalProps['onChange'] = formInput.onChange
  }

  return (
    <Grid
      item
      xs={size}
      container
      justifyContent={size === 12 ? 'center' : 'flex-start'}
      key={`formdata-input-${isTop ? 'top' : 'bottom'}-${formInput.name}-${idx}`}
    >
      <Input
        source={formInput.name}
        className={formInput.className}
        initialValue={formInput.value}
        choices={formInput.choices}
        validate={formInput.validate}
        label={formInput.label}
        reference={formInput.reference}
        {...conditionalProps}
      />
    </Grid>
  )
}

function getTransformedInputs(
  formDataInputs: BaseUploadButtonFormDataInput[] | undefined,
  isTop: boolean,
  values: any
): any {
  return (
    formDataInputs &&
    formDataInputs.map((formInput, idx) => {
      if (!!formInput.isTop === isTop) {
        const inputGridItem = getInputGridItem(formInput, idx, isTop)
        if (formInput.condition) {
          return formInput.condition(values) ? inputGridItem : null
        } else {
          return inputGridItem
        }
      }
      return null
    })
  )
}

export const BaseUploadButton = ({
  record,
  fileFieldInputs,
  formDataInputs,
  uploadFunction,
  dialogTitle,
  buttonLabel,
  deleteFunction,
  multiple,
  additionalPayloadData,
  buttonVariant,
  className,
  buttonClassNames,
  buttonIcon,
  Warning,
}: BaseUploadButtonProps) => {
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const handleClick = (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    setShowDialog(true)
  }
  const handleClose = () => setShowDialog(false)

  const useStyles = makeStyles({
    saveButton: {
      backgroundColor: '#002CB1',
      color: 'white',
      borderRadius: '570px',
      width: '40%',
    },
    dialog: {
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
  })

  const classes = useStyles()

  const handleSubmit = async (values: Record<string, any>) => {
    setLoading(true)

    const formData = new FormData()
    fileFieldInputs.forEach(({ name }) => {
      if (name === undefined) return
      let fileValue = get(values, name)
      if (multiple) {
        for (let attachment of fileValue) {
          if (attachment.rawFile) {
            formData.append(name, attachment.rawFile)
          }
        }
      } else {
        if (fileValue.rawFile) {
          formData.append(name, fileValue.rawFile)
        }
      }
    })

    if (formDataInputs) {
      for (let input of formDataInputs) {
        if (get(values, input.name)) {
          formData.append(input.name, get(values, input.name))
        }
      }
    }

    if (additionalPayloadData) {
      for (let payloadDataName of Object.keys(additionalPayloadData)) {
        formData.append(payloadDataName, get(additionalPayloadData, payloadDataName))
      }
    }

    let record_id = record?.id ? record?.id : ''
    uploadFunction(record_id, formData)
      .then(() => {
        setShowDialog(false)
        setLoading(false)
        notify('Document uploaded!', { type: 'success' })
        refresh()
      })
      .catch((error) => {
        notify(`Oops! Something went wrong: ${JSON.stringify(error?.response?.data)}`, { type: 'error' })
        captureExceptionIfProduction(error)
        setLoading(false)
      })
  }

  const handleDelete = async () => {
    if (deleteFunction && record) {
      setLoading(true)
      deleteFunction(record.id)
        .then(() => {
          setShowDialog(false)
          setLoading(false)
          notify('Document deleted!', { type: 'success' })
          refresh()
        })
        .catch((error) => {
          notify(`Oops! Something went wrong: ${JSON.stringify(error?.response?.data)}`, { type: 'error' })
          captureExceptionIfProduction(error)
          setLoading(false)
        })
    }
  }

  return (
    <>
      <RaButton
        onClick={handleClick}
        label={buttonLabel}
        variant={buttonVariant ? buttonVariant : 'contained'}
        className={`${className} ${buttonClassNames}`}
      >
        {buttonIcon}
      </RaButton>
      <Dialog
        className={classes.dialog}
        open={showDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') handleClose()
        }}
        aria-label={dialogTitle}
        fullWidth
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <Form
          onSubmit={handleSubmit}
          render={(formProps) => {
            const FormInputsTop = getTransformedInputs(formDataInputs, true, formProps.values)
            const FormInputsBottom = getTransformedInputs(formDataInputs, false, formProps.values)
            return (
              <>
                {Warning}
                <DialogContent>
                  {multiple && <DialogContentText>You can upload multiple attachments</DialogContentText>}
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Grid container spacing={1} justifyContent="center">
                      {FormInputsTop}
                    </Grid>
                    {fileFieldInputs.map((fileFieldInput: any, idx: number) => (
                      <FileInput
                        key={`dialog-file-input-${buttonLabel}-${idx}`}
                        source={fileFieldInput.name}
                        multiple={multiple}
                        accept="application/pdf,image/jpeg,image/png,image/gif"
                        validate={required()}
                        label={fileFieldInput.label}
                        initialValue={fileFieldInput.value}
                      >
                        <FileField title={fileFieldInput.name} />
                      </FileInput>
                    ))}
                    <Grid container spacing={1} justifyContent="center">
                      {FormInputsBottom}
                    </Grid>
                    {loading && <CircularProgress />}
                    {!loading && (
                      <>
                        <Button
                          type="submit"
                          onClick={formProps.handleSubmit}
                          disabled={loading}
                          variant="contained"
                          className={classes.saveButton}
                        >
                          Save
                        </Button>
                        <Box display="flex" flexDirection="row" alignItems="center">
                          {deleteFunction && record && (
                            <>
                              <Button onClick={handleDelete} disabled={loading}>
                                Delete
                              </Button>
                              <VerticalDivider />
                            </>
                          )}
                          <Button onClick={handleClose} disabled={loading}>
                            Cancel
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </DialogContent>
              </>
            )
          }}
        />
      </Dialog>
    </>
  )
}
